import type { SupabaseClient } from '@supabase/supabase-js';
import type { AppState } from './types';

/** Replace only the current member's avatar pointer, preserving pending local work. */
export function mergeOwnAvatar(state: AppState, userId: string, mediaId: string | null): AppState {
  if (state.current_user_id !== userId) return state;
  const index = state.members.findIndex((member) => member.id === userId);
  if (index < 0 || state.members[index].avatar_media_id === (mediaId ?? undefined)) return state;
  const members = [...state.members];
  members[index] = { ...members[index], avatar_media_id: mediaId ?? undefined };
  return { ...state, members };
}

/** Background reads never reload the account or commit a response for another session. */
export function createOwnAvatarRefresh(
  client: SupabaseClient,
  userId: string,
  access: {
    getViewerId: () => string | undefined;
    getState: () => AppState;
    commit: (state: AppState) => void;
  },
) {
  let active: AbortController | null = null;
  let disposed = false;
  async function refresh(): Promise<boolean> {
    if (disposed || access.getViewerId() !== userId) return false;
    active?.abort();
    const controller = new AbortController();
    active = controller;
    try {
      const result = await client
        .from('profiles')
        .select('id,avatar_media_id')
        .eq('id', userId)
        .abortSignal(controller.signal)
        .maybeSingle();
      if (
        disposed ||
        controller.signal.aborted ||
        access.getViewerId() !== userId ||
        result.error ||
        result.data?.id !== userId
      )
        return false;
      const current = access.getState();
      const next = mergeOwnAvatar(current, userId, result.data.avatar_media_id ?? null);
      if (next === current) return false;
      access.commit(next);
      return true;
    } catch {
      // A focus refresh must not interrupt a draft or replace the account's state.
      return false;
    }
  }
  return {
    refresh,
    dispose() {
      disposed = true;
      active?.abort();
    },
  };
}
